/**
 * Outfits and mascot › Recognition celebrations (GET and PUT /v1/admin/desktop/mascot/celebrations;
 * docs/PLAN-recognition-celebration-2026-10-21.md section 4.5; owner, 2026-10-21): the celebration packs the import tool
 * registered, each with its name, theme, outfit, duration, size and an on/off switch, and the registry revision. One
 * Save sends every switch with the revision the section loaded. Hidden on a service with the feature off (404). The
 * words and the body are in lib/celebrations.ts.
 */
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useState } from 'react';
import { Check, PartyPopper, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  CELEBRATIONS_CHANGED_TEXT,
  CELEBRATIONS_EMPTY_TEXT,
  CELEBRATIONS_HINT,
  CELEBRATIONS_NONE_ON_TEXT,
  CELEBRATIONS_SAVED_TEXT,
  CELEBRATIONS_UNREADABLE_TEXT,
  bytesText,
  celebrationsChanged,
  celebrationsSaveBody,
  durationText,
  enabledCount,
  enabledSummaryText,
  switchesOf,
  type CelebrationPackRow,
  type CelebrationSwitches,
  type CelebrationsView,
} from '../lib/celebrations';
import { Button, Callout, EmptyState, Icon, Panel, Skeleton, Switch, Table, Tag, type Column } from '../ui';
import { SaveNote } from './SaveNote';

export interface CelebrationsSectionProps {
  /** Writes are off: the admin is an auditor, or the card is busy. */
  locked: boolean;
}

type Read =
  | { status: 'loading' }
  /** A service with recognition celebrations off (404): the section is not shown. */
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: CelebrationsView };

export function CelebrationsSection({ locked }: CelebrationsSectionProps) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [switches, setSwitches] = useState<CelebrationSwitches>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  /** Show what the service holds: the packs and their switches as stored. */
  const show = useCallback((view: CelebrationsView) => {
    setRead({ status: 'loaded', view });
    setSwitches(switchesOf(view));
  }, []);

  useEffect(() => {
    let live = true;
    api.desktopMascotCelebrations().then(
      (view) => {
        if (!live) return;
        if (view === null) setRead({ status: 'absent' });
        else show(view);
      },
      (error: unknown) => {
        if (live) setRead({ status: 'failed', text: errorText(error, CELEBRATIONS_UNREADABLE_TEXT) });
      },
    );
    return () => {
      live = false;
    };
  }, [show]);

  if (read.status === 'absent') return null;
  const view = read.status === 'loaded' ? read.view : null;
  const disabled = locked || busy || view === null;
  const dirty = view !== null && celebrationsChanged(switches, view);
  const on = view ? enabledCount(switches, view) : 0;

  const cancel = () => {
    if (!view) return;
    setSwitches(switchesOf(view));
    setNote(null);
  };
  const save = async () => {
    if (!view) return;
    setBusy(true);
    setNote(null);
    try {
      show(await api.saveDesktopMascotCelebrations(celebrationsSaveBody(switches, view)));
      setNote({ ok: true, text: CELEBRATIONS_SAVED_TEXT });
    } catch (error) {
      const code = error instanceof ApiError && typeof error.body?.error === 'string' ? error.body.error : undefined;
      if (code === 'celebrations_changed') {
        setNote({ ok: false, text: CELEBRATIONS_CHANGED_TEXT });
        try {
          const fresh = await api.desktopMascotCelebrations();
          if (fresh) show(fresh);
        } catch {
          // The note already says to check it again; a reload shows it.
        }
      } else {
        setNote({ ok: false, text: errorText(error, 'Nothing was saved.') });
      }
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<CelebrationPackRow>[] = [
    { key: 'name', header: 'Pack', cell: (pack) => <span data-pack-name={pack.id}>{pack.name}</span>, width: '26%' },
    { key: 'theme', header: 'Theme', cell: (pack) => pack.theme, muted: true },
    { key: 'outfit', header: 'Outfit', cell: (pack) => pack.outfit, muted: true, hideOnPhone: true },
    { key: 'duration', header: 'Length', cell: (pack) => durationText(pack.durationMs), numeric: true },
    { key: 'bytes', header: 'Size', cell: (pack) => bytesText(pack.bytes), numeric: true },
    {
      key: 'enabled',
      header: 'On',
      cell: (pack) => (
        <Switch
          checked={switches[pack.id] ?? pack.enabled}
          disabled={disabled}
          label={`${pack.name} on`}
          labelHidden
          stateLabels={['On', 'Off']}
          onChange={(checked) => { setSwitches((current) => ({ ...current, [pack.id]: checked })); setNote(null); }}
        />
      ),
      align: 'end',
    },
  ];

  return (
    <Panel
      heading="h3"
      titleId="mascot-celebrations-title"
      title={<span className="ui-title-icon"><Icon icon={PartyPopper} size="md" />Recognition celebrations</span>}
      description={CELEBRATIONS_HINT}
      actions={view ? <Tag mono={false} title="The registry revision every save is checked against">revision {view.revision}</Tag> : undefined}
    >
      {read.status === 'loading' && <div className="ui-form" aria-hidden="true"><Skeleton size="md" width="240px" /><Skeleton size="lg" /></div>}
      {read.status === 'failed' && <Callout tone="warn" role="alert">{read.text}</Callout>}
      {view && (
        <fieldset disabled={disabled} className="ui-fieldset">
          <div className="ui-form">
            <Table
              label="Recognition celebration packs"
              columns={columns}
              rows={view.packs}
              rowKey={(pack) => pack.id}
              rowId={(pack) => `celebration-${pack.id}`}
              stacked
              empty={<EmptyState icon={PartyPopper} title="No celebration packs" compact>{CELEBRATIONS_EMPTY_TEXT}</EmptyState>}
            />
            {view.packs.length > 0 && (
              <p className={on > 0 ? 'ui-note is-ok' : 'ui-note is-warn'} data-celebrations-on={on}>
                <span>{on === 0 ? `${enabledSummaryText(on, view.packs.length)}. ${CELEBRATIONS_NONE_ON_TEXT}` : `${enabledSummaryText(on, view.packs.length)}.`}</span>
              </p>
            )}
            <div className="ui-actions">
              <Button variant="primary" size="sm" icon={Check} busy={busy} disabled={!dirty || disabled} onClick={() => void save()}>Save</Button>
              <Button variant="ghost" size="sm" icon={X} disabled={!dirty || disabled} onClick={cancel}>Cancel</Button>
              {dirty && <span className="ui-cell-sub">Not saved yet.</span>}
            </div>
          </div>
        </fieldset>
      )}
      <SaveNote note={note} />
    </Panel>
  );
}
